<script>
	import { res } from '../../stores';
	import BotCard from './BotCard.svelte';
	import BotRow from './BotRow.svelte';
	import OperationText from '../ops/OperationText.svelte';
	import { isSuit } from '../types';

	export let p;
	export let onPick = () => {}; // (event, row, key) for clicking an effect

	// Each physical bot card is double-sided: the trait card flips between stages (Khan, Shaw),
	// any card with split rows flips between non-DO/DO (or Borg Queen's collective < 20 / >= 20)
	$: charts = $res.botCharts[p.captain];
	$: stages = Object.keys(charts).filter(k => k != 'chartName' && Array.isArray(charts[k]));
	$: activeStage = stages.includes(p.currentChart) ? p.currentChart : stages[0];
	$: isBQ = charts[activeStage].some(r => r['collective<20']);
	$: sides = isBQ
		? [{key: 'collective<20', label: ': Collective < 20'}, {key: 'collective>=20', label: ': Collective 20+'}]
		: [{key: 'noDO', label: ''}, {key: 'DO', label: ' with Duty Officer'}];
	$: activeSide = isBQ ? (p.collective >= 20 ? 1 : 0) : (p.decks.duty.length > 0 ? 1 : 0);

	// Reset to what's in effect whenever that changes; flipping only previews
	let stage, traitSide, suitSide, syncSide;
	$: stage = activeStage;
	$: traitSide = suitSide = syncSide = activeSide;

	$: rows = charts[stage].filter(r => r.basic || r[sides[0].key] || r[sides[1].key]);
	// A stage with no split rows at all (Khan in exile) is a single card
	$: suitRows = rows.some(r => !r.basic) ? rows.filter(r => isSuit(r.icon)) : [];
	$: syncRows = rows.filter(r => r.syncLevel); // Borg Queen's separate Sync card
	$: traitRows = rows.filter(r => !suitRows.includes(r) && !syncRows.includes(r));
	$: traitSplit = traitRows.some(r => !r.basic);
	$: suitSplit = suitRows.some(r => !r.basic);
	$: syncSplit = syncRows.some(r => !r.basic);
	$: cardCount = 1 + (suitRows.length > 0) + (syncRows.length > 0);
	$: stageLabel = stages.length > 1 && stage != p.captain.toLowerCase() ? `: ${stage}` : '';
	$: rules = $res.botSpecialRules || {};
	$: specialRule = rules[`${p.captain}-${stage}`.toLowerCase()] || rules[p.captain.toLowerCase()];

	// Rows without a split just have a basic effect
	function effectKey(row, sideKey){
		return row.basic ? 'basic' : sideKey;
	}
	function effectText(row, sideKey){
		let e = row[effectKey(row, sideKey)];
		return e ? e.text : '';
	}

	function flipStage(){
		stage = stages[(stages.indexOf(stage) + 1) % stages.length];
	}
</script>

<div class="bot-chart" style="--bot-h: min(85vh, 85vw * 600 / {cardCount * 358 - 8});">
	<BotCard deck={p.captain} title={'Traits' + stageLabel + (traitSplit ? sides[traitSide].label : '')}
		preview={stage != activeStage || traitSplit && traitSide != activeSide}
		onFlip={stages.length > 1 ? flipStage : traitSplit ? () => traitSide = 1 - traitSide : null}>
		<div class="outer">
			<div class="inner">
				<div class="pill surprise">
					<OperationText text="[[surprise]] Resolve this card's Surprise operation." size={15} color="white" />
				</div>
				{#each traitRows as row}
					<BotRow {row} text={effectText(row, sides[traitSide].key)} on:click={ev => onPick(ev, row, effectKey(row, sides[traitSide].key))} />
				{/each}
				{#if specialRule}
					<div class="pill special">
						{#each specialRule.split('#newline').map(x => x.trim()) as part}
							<OperationText text={part} color="white" size={12} />
						{/each}
					</div>
				{/if}
			</div>
		</div>
		<div class="footer"></div>
	</BotCard>
	{#if suitRows.length > 0}
		<BotCard deck={p.captain} title={'Suits' + (suitSplit ? sides[suitSide].label : '')}
			preview={stage != activeStage || suitSplit && suitSide != activeSide}
			onFlip={suitSplit ? () => suitSide = 1 - suitSide : null}>
			<div class="suits">
				{#each suitRows as row}
					<BotRow {row} text={effectText(row, sides[suitSide].key)} on:click={ev => onPick(ev, row, effectKey(row, sides[suitSide].key))} />
				{/each}
			</div>
		</BotCard>
	{/if}
	{#if syncRows.length > 0}
		<BotCard deck={p.captain} title={'Sync' + (syncSplit ? sides[syncSide].label : '')}
			preview={stage != activeStage || syncSplit && syncSide != activeSide}
			onFlip={syncSplit ? () => syncSide = 1 - syncSide : null}>
			<div class="suits">
				{#each syncRows as row}
					<BotRow {row} text={effectText(row, sides[syncSide].key)} on:click={ev => onPick(ev, row, effectKey(row, sides[syncSide].key))} />
				{/each}
			</div>
		</BotCard>
	{/if}
</div>

<style lang="scss">
	.bot-chart {
		display: flex;
		gap: calc(var(--bot-h) / 75);
		justify-content: center;
	}
	.outer {
		flex-grow: 1;
		min-height: 0;
		background-color: skyblue;
		border-top: 2px solid azure;
		border-bottom: 1px solid azure;
		padding: calc(var(--u) * 3) calc(var(--u) * 8) calc(var(--u) * 3) 0;
	}
	.inner {
		flex-grow: 1;
		background-color: lightgrey;
		border-radius: 0 calc(var(--u) * 8) calc(var(--u) * 8) 0;
		border: 1px solid azure;
		border-left: 0;
		overflow: auto;
		flex-direction: column;
		padding: calc(var(--u) * 2) calc(var(--u) * 2) calc(var(--u) * 2) 0;
		gap: calc(var(--u) * 4);
	}
	.footer {
		height: 1em;
		flex-shrink: 0;
	}
	.suits {
		flex-grow: 1;
		background-color: lightgrey;
		flex-direction: column;
		overflow: auto;
	}
	.pill {
		border-radius: calc(var(--u) * 20);
		justify-content: center;
		align-self: center;
		flex-shrink: 0;
	}
	.surprise {
		background-color: darkred;
		width: fit-content;
		padding: calc(var(--u) * 5) 1em;
		box-shadow: inset 0 0 0 1px darkred, inset 0 0 0 2px white;
	}
	.special {
		flex-direction: column;
		gap: calc(var(--u) * 4);
		background-color: darkslategrey;
		width: 80%;
		padding: 0.75em 1em;
		box-shadow: inset 0 0 0 1px darkslategrey, inset 0 0 0 2px white;
	}
</style>
